// Shrinks a photo in the browser before it is uploaded.
//
// The album never renders anything larger than Photo::FULL_LIMIT (2000px), so a phone camera
// sends roughly ten times the pixels the server ends up keeping. Resizing here turns a 40MB
// export into about 1MB, which matters most on the patchy connections the installable app is
// built for, and puts the 20MB limit out of reach for ordinary photos.
//
// This is a convenience, not a control. Photo's size and content-type validations still run,
// since anything here can be bypassed by posting directly.

import UTIF from 'utif'

const MAX_EDGE = 2000
const QUALITY = 0.85

// Below this, re-encoding costs more time than it saves in bytes.
const SKIP_BELOW_BYTES = 1024 * 1024

// Returns { file, undecodable }. `undecodable` means this browser could not read the image at
// all, which is the difference between "too big" and "wrong format" in the message the picker
// shows — a distinction worth keeping, since one is fixable by the person uploading.
export default async function downscaleImage(file) {
  if (!file?.type?.startsWith('image/')) return { file, undecodable: false }
  if (file.type === 'image/gif') return { file, undecodable: false } // Re-encoding would flatten it.
  if (file.size < SKIP_BELOW_BYTES) return { file, undecodable: false }

  try {
    const bitmap = await decodeToBitmap(file)
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)

    const context = canvas.getContext('2d')
    // JPEG has no alpha channel: without this, transparent pixels encode as black.
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY))

    // An already-optimised JPEG can come back larger than it went in.
    if (!blob || blob.size >= file.size) return { file, undecodable: false }

    return {
      file: new File([blob], toJpegName(file.name), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      }),
      undecodable: false,
    }
  } catch {
    // HEIC outside Safari, TIFF outside WebKit, or an image too large for this browser's
    // canvas. Hand back the original and let the server accept or reject it — but say so, so
    // an unreadable format is not reported as an oversized file.
    return { file, undecodable: true }
  }
}

// Browsers decode their own formats far faster than any JS decoder, so this only reaches for
// UTIF when the native path has already failed — in practice, TIFF outside WebKit.
async function decodeToBitmap(file) {
  try {
    // `from-image` applies the EXIF rotation during decode. Without it every portrait photo
    // off an iPhone arrives on its side, because the canvas drops the orientation tag.
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch (error) {
    if (!isTiff(file)) throw error
    return await decodeTiff(file)
  }
}

async function decodeTiff(file) {
  const buffer = await file.arrayBuffer()

  const pages = UTIF.decode(buffer)
  if (!pages.length) throw new Error('TIFF has no pages')

  // Multi-page TIFFs exist, but a photo export is always the first page.
  const page = pages[0]
  UTIF.decodeImage(buffer, page)
  const rgba = UTIF.toRGBA8(page)
  if (!rgba?.length) throw new Error('TIFF page has no pixels')

  const pixels = new ImageData(new Uint8ClampedArray(rgba.buffer), page.width, page.height)
  return await createImageBitmap(pixels)
}

function isTiff(file) {
  return file.type === 'image/tiff' || /\.tiff?$/i.test(file.name || '')
}

function toJpegName(name) {
  const stem = (name || '').replace(/\.[^./\\]+$/, '')
  return `${stem || 'photo'}.jpg`
}
